import { useEffect, useRef } from "react";

/**
 * Tells the embed host when the panel opens or closes: React `onOpen` / `onClose`, the
 * web-component's `wexio:open` / `wexio:close` events and, through the iframe's postMessage,
 * the script-tag `wexio:open` / `wexio:close` window events.
 *
 * Driven by the `isOpen` TRANSITION, not by individual controls, so every path reports the
 * same way: launcher, header X, Escape, peek tap, notification click. `enabled` is false in
 * demo / preview modes, where the dashboard preview must not fire the host's analytics.
 */
export function useOpenCloseEvents({
  isOpen,
  enabled,
  onOpen,
  onClose,
}: {
  isOpen: boolean;
  enabled: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}): void {
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  onOpenRef.current = onOpen;
  onCloseRef.current = onClose;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const last = useRef(false);

  useEffect(() => {
    if (isOpen === last.current) return;
    last.current = isOpen;
    if (!enabled) return;
    if (isOpen) onOpenRef.current?.();
    else onCloseRef.current?.();
  }, [isOpen, enabled]);

  // A panel torn down while open (the host changed the public key, or removed the widget) never
  // sees its own close transition: report it, so a host that mirrors `onOpen` / `onClose` into its
  // own state does not stay stale. `last` is reset so a StrictMode re-run reports the open again.
  useEffect(
    () => () => {
      if (!last.current) return;
      last.current = false;
      if (enabledRef.current) onCloseRef.current?.();
    },
    [],
  );
}
